import {
  faArrowProgress,
  faCloudPlus,
  faCode,
  faCog,
  faDatabase,
  faList,
  faListTree,
  faSave,
  faShareFromSquare,
  faSquareList,
  faTable,
  faWindowFlip,
} from "@fortawesome/pro-duotone-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { batch } from "@preact/signals-react";
import {
  Button,
  Chip,
  Toolbar,
  ToolbarSeparator,
  ToolbarSpacer,
} from "@progress/kendo-react-buttons";
import { Popover } from "@progress/kendo-react-tooltip";
import { TreeView } from "@progress/kendo-react-treeview";
import { DiagramModel } from "@projectstorm/react-diagrams";
import axios from "axios";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  advancedOptionsEnabled,
  allTemplates,
  notification,
  userEmail,
  userTemplates,
} from "../Common/Store/CommonSignals";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { If } from "../Common/UI/IfComponent";
import { handleSignalRTrigger } from "../Utilities/CommonUtils";
import {
  automationSessionData,
  dataTreeView,
  engine,
  expandedTreeItems,
  isTemplatePersisted,
  selectedTemplateMenuItem,
  showAddDataPopover,
  showAutomationsWindow,
  templateData,
  templateEditMode,
  templateInformationPaneVisible,
  validationError,
} from "./NewTemplateSignals";
import { validateForm } from "./TemplateValidator";

export const TemplateRibbonBar = () => {
  const navigate = useNavigate();
  const anchor = useRef();

  const onExpandChange = (event) => {
    expandedTreeItems.value = {
      ...expandedTreeItems.value,
      [event.item.text]: !expandedTreeItems.value[event.item.text],
    };
  };

  const customItem = ({ e }) => {
    if (e.item.title) return <div className="k-mt-2 k-mb-2 k-ml-2">{e.item.text}</div>;
    if (!e.item?.type) return;

    let icon = "";
    switch (e.item.type) {
      case "Template":
        icon = faWindowFlip;
        break;
      case "List":
        icon = faList;
        break;
      case "Table":
        icon = faTable;
        break;
      case "Global Parameters":
        icon = faCog;
        break;
      default:
        icon = faDatabase;
        break;
    }

    return (
      <Chip
        className="k-ml-0 k-p-0"
        onClick={() => {
          let columns = {};
          if (e.item.type === "Template") {
            const selectedTemplate = userTemplates.value?.find((template) => {
              return template.id === e.item.id;
            });

            columns["id"] = { selected: true };
            columns["createdDate"] = { selected: true };
            columns["_ts"] = { selected: true };
            columns["entryAuthor"] = { selected: true };

            selectedTemplate.sections?.forEach((section) => {
              section.fields?.forEach((field) => {
                columns[field.fieldName] = { selected: true };
              });
            });
          } else {
            columns = {
              value: { selected: true },
              filterValue: { selected: true },
            };
          }
          batch(() => {
            templateData.value = {
              ...templateData.value,
              properties: {
                ...templateData.value.properties,
                DataConnections: [
                  ...(templateData.value.properties?.DataConnections ?? []),
                  {
                    id: e.item.id,
                    text: e.item.text,
                    type: e.item.type,
                    columns,
                    filter: null,
                    sort: [],
                  },
                ],
              },
            };
            showAddDataPopover.value = false;
            templateInformationPaneVisible.value = true;
            selectedTemplateMenuItem.value = "Data Connections";
          });
        }}
      >
        <div className="k-d-flex">
          <FontAwesomeIcon
            className="k-mr-2"
            style={{
              "--fa-primary-color": "#0078d4",
              "--fa-secondary-color": "#a6e6ff",
              "--fa-secondary-opacity": 1,
            }}
            icon={icon}
            fontSize={16}
          />
          {e.item.text}
        </div>
      </Chip>
    );
  };

  function resetAllowFieldNameEditProperty() {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, AllowFieldNameEdit: false },
    };
  }

  async function handleSubmit() {
    if (!validateForm({ templateFormData: templateData.value })) return;
    resetAllowFieldNameEditProperty();

    if (isTemplatePersisted.value) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_ENDPOINT}/Template/${templateData.value.id}?userId=${userEmail.value}`,
          templateData.value
        );
        notification.value = {
          show: true,
          type: "success",
          message: "Template updated successfully!",
        };

        await handleSignalRTrigger("UPDATE", "Template", response.data);

        allTemplates.value = allTemplates.value.map((template) =>
          template.id === templateData.value.id ? templateData.value : template
        );
      } catch (error) {
        notification.value = {
          show: true,
          type: "error",
          message: "Failed to update template!",
          error: error,
        };
        throw error;
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_ENDPOINT}/Template?userId=${userEmail.value}`,
          templateData.value
        );
        notification.value = {
          show: true,
          type: "success",
          message: "Template created successfully!",
        };

        await handleSignalRTrigger("CREATE", "Template", response.data);

        allTemplates.value = [...allTemplates.value, response.data];
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          console.error("Error response data:", error.response);
        }
        notification.value = {
          show: true,
          type: "error",
          message: "Failed to create template!",
          error: error,
        };
        throw error;
      }
    }
    validationError.value = "";
    navigate("/templates");
  }

  return (
    <div>
      <Toolbar size={null}>
        <ColorChangingIcon
          title="Save Template"
          name="btnTemplateSubmit"
          icon={faSave}
          initialPrimaryColor="#000"
          initialSecondaryColor="#5fd255"
          primaryOpacity={0.75}
          secondaryOpacity={1}
          initialSize={32}
          hoverSize={32}
          margin="0 10px 0 10px"
          onClick={handleSubmit}
        />
        <div className="k-mx-2 k-text-error">{validationError.value}</div>
        <ToolbarSeparator />
        <Button
          className="!k-fs-xl !k-rounded-0"
          fillMode={"flat"}
          ref={anchor}
          onClick={(e) => {
            e.stopPropagation();
            showAddDataPopover.value = !showAddDataPopover.value;
          }}
        >
          <FontAwesomeIcon
            icon={faCloudPlus}
            className="k-mr-1"
          />
          Add Data
          <FontAwesomeIcon
            icon={showAddDataPopover.value ? faChevronUp : faChevronDown}
            className="k-ml-1"
          />
        </Button>
        <Button
          className="!k-fs-xl !k-rounded-0"
          fillMode={"flat"}
          onClick={() => {
            batch(() => {
              templateInformationPaneVisible.value = true;
              selectedTemplateMenuItem.value = "Tree View";
            });
          }}
        >
          <FontAwesomeIcon
            icon={faListTree}
            className="k-mr-1"
          />
          Template Hierarchy Tree
        </Button>
        <Button
          className="!k-fs-xl !k-rounded-0"
          fillMode={"flat"}
          onClick={() => {
            batch(() => {
              selectedTemplateMenuItem.value = "Permissions";
              templateInformationPaneVisible.value = true;
            });
          }}
        >
          <FontAwesomeIcon
            icon={faShareFromSquare}
            className="k-mr-1"
          />
          Share Template
        </Button>
        <Button
          className="!k-fs-xl !k-rounded-0"
          fillMode={"flat"}
          onClick={() => {
            batch(() => {
              selectedTemplateMenuItem.value = "Template Properties";
              templateInformationPaneVisible.value = true;
            });
          }}
        >
          <FontAwesomeIcon
            icon={faSquareList}
            className="k-mr-1"
          />
          Template Properties
        </Button>
        <Button
          className="!k-fs-xl !k-rounded-0"
          fillMode={"flat"}
          onClick={() => {
            batch(() => {
              showAutomationsWindow.value = true;
              automationSessionData.value =
                templateData.value.properties.Automations?.map((automation) => {
                  const newModel = new DiagramModel();
                  newModel.deserializeModel(automation.model, engine.value);
                  return {
                    ...automation,
                    model: newModel,
                  };
                }) ?? [];
            });
          }}
        >
          <FontAwesomeIcon
            icon={faArrowProgress}
            className="k-mr-1"
            flip="vertical"
          />
          Automations
        </Button>
        <ToolbarSeparator />
        <ToolbarSpacer />

        <If condition={advancedOptionsEnabled.value}>
          <ToolbarSeparator />
          <Button
            className="!k-fs-xl !k-rounded-0"
            fillMode={"flat"}
            onClick={() => {
              if (templateEditMode.value === "Visual") {
                templateEditMode.value = "Code";
              } else {
                templateEditMode.value = "Visual";
              }
            }}
          >
            <FontAwesomeIcon
              icon={faCode}
              className="k-mr-1"
              flip="vertical"
            />
          </Button>
        </If>
      </Toolbar>
      <div onClick={() => (showAddDataPopover.value = false)} />
      <Popover
        show={showAddDataPopover.value}
        anchor={anchor?.current?.element}
        position={"bottom"}
        callout={false}
        title={"Add a Data Connection"}
        animate={false}
        style={{
          width: "350px",
          maxHeight: "500px",
          overflowY: "auto",
          boxShadow: "0px 4px 18px #888888",
        }}
        offset={{ left: 0, top: 0 }}
      >
        <div>
          <TreeView
            className="data-connection-tree-view"
            data={dataTreeView.value}
            expandIcons={true}
            onExpandChange={onExpandChange}
            item={(e) => customItem({ e })}
          />
        </div>
      </Popover>
    </div>
  );
};
